/**
 * Digital Company — layout document helpers.
 *
 * Parsing, ids, hit-testing, resize math, and the "add this piece" presets
 * used by the super-admin editor. Geometry types live in `map.ts`.
 */

import {
  FURNITURE_KINDS,
  WORLD,
  cloneLayout,
  defaultLayout,
  ensurePingPongTable,
  type DoorKind,
  type FloorKind,
  type FurnitureKind,
  type LayoutDoor,
  type LayoutFloor,
  type LayoutFurniture,
  type LayoutWall,
  type OfficeLayout,
  type Rect,
  type Zone,
} from './map';
import {
  angleDeg,
  facingToRotation,
  normalizeDeg,
  pieceRotation,
  pointInRotatedRect,
  rectCenter,
  rotatePoint,
  rotationToFacing,
  snapRotation,
  worldDeltaToLocal,
} from './rotation';

export type LayoutLayer = 'wall' | 'furniture' | 'zone' | 'door' | 'floor' | 'station';

export interface LayoutSelection {
  layer: LayoutLayer;
  id: string;
}

export type PlaceKind =
  | 'wall'
  | 'rack'
  | 'desk'
  | 'bench'
  | 'machine'
  | 'square'
  | 'circle'
  | 'table'
  | 'counter'
  | 'plant'
  | 'couch'
  | 'chair'
  | 'coffee'
  | 'whiteboard'
  | 'pingpong'
  | 'zone'
  | 'station'
  | 'door';

export type ResizeHandle = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

export const RESIZE_HANDLES: readonly ResizeHandle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

const KIND_SET = new Set<string>(FURNITURE_KINDS);
const FLOOR_KINDS: readonly FloorKind[] = ['concrete', 'carpet', 'wood', 'pavement', 'rug'];
const DOOR_KINDS: readonly DoorKind[] = ['door', 'dock', 'entrance'];
const FACINGS = new Set(['up', 'down', 'left', 'right']);

export function newLayoutId(prefix: string): string {
  const rand =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return `${prefix}-${rand}`;
}

function asNum(v: unknown, fallback: number): number {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return Number.isFinite(n) ? n : fallback;
}

function asStr(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

function readRotation(raw: unknown): number | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const n = asNum((raw as { rotation?: unknown }).rotation, NaN);
  if (!Number.isFinite(n)) return undefined;
  const deg = normalizeDeg(n);
  return deg === 0 ? undefined : deg;
}

function clampRect(r: Rect, minW: number, minH: number): Rect {
  const w = Math.max(minW, r.w);
  const h = Math.max(minH, r.h);
  const x = Math.max(-20, Math.min(WORLD.w - 4, r.x));
  const y = Math.max(-20, Math.min(WORLD.h - 4, r.y));
  return { x, y, w, h };
}

function readRect(raw: unknown): Rect | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const x = asNum(o.x, NaN);
  const y = asNum(o.y, NaN);
  const w = asNum(o.w, NaN);
  const h = asNum(o.h, NaN);
  if (![x, y, w, h].every(Number.isFinite)) return null;
  return { x, y, w, h };
}

/**
 * Coerce unknown JSON into a usable OfficeLayout. Missing pieces fall back
 * to the built-in plan so a partial / corrupt save never blanks the map.
 */
export function parseLayout(raw: unknown): OfficeLayout {
  const fallback = defaultLayout();
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return fallback;
  const o = raw as Record<string, unknown>;

  const floors = Array.isArray(o.floors)
    ? o.floors
        .map((item, i): LayoutFloor | null => {
          const r = readRect(item);
          if (!r || typeof item !== 'object' || !item) return null;
          const rec = item as Record<string, unknown>;
          const kind = FLOOR_KINDS.includes(rec.kind as FloorKind)
            ? (rec.kind as FloorKind)
            : 'concrete';
          const color = typeof rec.color === 'string' ? rec.color : undefined;
          return { id: asStr(rec.id, `floor-${i}`), kind, color, ...r };
        })
        .filter((x): x is LayoutFloor => x != null)
    : null;

  const walls = Array.isArray(o.walls)
    ? o.walls
        .map((item, i): LayoutWall | null => {
          const r = readRect(item);
          if (!r) return null;
          const id =
            item && typeof item === 'object'
              ? asStr((item as { id?: unknown }).id, `wall-${i}`)
              : `wall-${i}`;
          return { id, ...r, rotation: readRotation(item) };
        })
        .filter((x): x is LayoutWall => x != null)
    : fallback.walls;

  const doors = Array.isArray(o.doors)
    ? o.doors
        .map((item, i): LayoutDoor | null => {
          const r = readRect(item);
          if (!r || typeof item !== 'object' || !item) return null;
          const rec = item as Record<string, unknown>;
          const kind = DOOR_KINDS.includes(rec.kind as DoorKind) ? (rec.kind as DoorKind) : 'door';
          return { id: asStr(rec.id, `door-${i}`), kind, ...r, rotation: readRotation(rec) };
        })
        .filter((x): x is LayoutDoor => x != null)
    : fallback.doors;

  const furniture = Array.isArray(o.furniture)
    ? o.furniture
        .map((item, i): LayoutFurniture | null => {
          const r = readRect(item);
          if (!r || typeof item !== 'object' || !item) return null;
          const rec = item as Record<string, unknown>;
          const kind = KIND_SET.has(asStr(rec.kind)) ? (rec.kind as FurnitureKind) : null;
          if (!kind) return null;
          const facing = FACINGS.has(asStr(rec.facing))
            ? (rec.facing as LayoutFurniture['facing'])
            : undefined;
          return {
            id: asStr(rec.id, `furn-${i}`),
            kind,
            label: typeof rec.label === 'string' ? rec.label : undefined,
            solid: rec.solid === false ? false : rec.solid === true ? true : undefined,
            facing,
            rotation: readRotation(rec),
            ...r,
          };
        })
        .filter((x): x is LayoutFurniture => x != null)
    : fallback.furniture;

  const zones = Array.isArray(o.zones)
    ? o.zones
        .map((item, i): Zone | null => {
          const r = readRect(item);
          if (!r || typeof item !== 'object' || !item) return null;
          const rec = item as Record<string, unknown>;
          const labelAt =
            rec.labelAt && typeof rec.labelAt === 'object'
              ? {
                  x: asNum((rec.labelAt as { x?: unknown }).x, r.x + r.w / 2),
                  y: asNum((rec.labelAt as { y?: unknown }).y, r.y + r.h / 2),
                }
              : undefined;
          return {
            id: asStr(rec.id, `zone-${i}`),
            label: asStr(rec.label, 'Room'),
            showLabel: rec.showLabel !== false,
            labelAt,
            ...r,
          };
        })
        .filter((x): x is Zone => x != null)
    : null;

  let stationSizes: OfficeLayout['stationSizes'];
  if (o.stationSizes && typeof o.stationSizes === 'object' && !Array.isArray(o.stationSizes)) {
    stationSizes = {};
    for (const [id, val] of Object.entries(o.stationSizes as Record<string, unknown>)) {
      if (!val || typeof val !== 'object') continue;
      const w = asNum((val as { w?: unknown }).w, NaN);
      const h = asNum((val as { h?: unknown }).h, NaN);
      if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
        const rotation = readRotation(val);
        stationSizes[id] = rotation ? { w, h, rotation } : { w, h };
      }
    }
  }

  return ensurePingPongTable({
    version: 1,
    floors: floors ?? fallback.floors,
    walls,
    doors,
    furniture,
    zones: zones ?? fallback.zones,
    stationSizes,
  });
}

export function layoutsEqual(a: OfficeLayout, b: OfficeLayout): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

const PLACE_SIZE: Record<Exclude<PlaceKind, 'station'>, { w: number; h: number }> = {
  wall: { w: 80, h: 6 },
  rack: { w: 34, h: 43 },
  desk: { w: 44, h: 18 },
  bench: { w: 56, h: 12 },
  machine: { w: 48, h: 32 },
  square: { w: 32, h: 32 },
  circle: { w: 32, h: 32 },
  table: { w: 36, h: 24 },
  counter: { w: 64, h: 20 },
  plant: { w: 12, h: 12 },
  couch: { w: 60, h: 18 },
  chair: { w: 12, h: 12 },
  coffee: { w: 40, h: 14 },
  whiteboard: { w: 40, h: 6 },
  pingpong: { w: 36, h: 58 },
  zone: { w: 90, h: 70 },
  door: { w: 20, h: 8 },
};

const FURNITURE_PLACE: Partial<Record<PlaceKind, FurnitureKind>> = {
  rack: 'rack',
  desk: 'desk',
  bench: 'bench',
  machine: 'machine',
  square: 'square',
  circle: 'circle',
  table: 'table',
  counter: 'counter',
  plant: 'plant',
  couch: 'couch',
  chair: 'chair',
  coffee: 'coffee',
  whiteboard: 'whiteboard',
  pingpong: 'pingpong',
};

const SOLID_DEFAULT: Partial<Record<FurnitureKind, boolean>> = {
  chair: false,
  whiteboard: false,
};

export function placeSize(kind: PlaceKind): { w: number; h: number } {
  if (kind === 'station') return { w: 32, h: 14 };
  return PLACE_SIZE[kind];
}

export function minSizeFor(layer: LayoutLayer): { w: number; h: number } {
  if (layer === 'wall') return { w: 4, h: 4 };
  if (layer === 'zone' || layer === 'floor') return { w: 16, h: 16 };
  if (layer === 'station') return { w: 16, h: 8 };
  if (layer === 'door') return { w: 6, h: 4 };
  return { w: 8, h: 8 };
}

export function placeLabel(kind: PlaceKind): string {
  switch (kind) {
    case 'wall':
      return 'Wall';
    case 'rack':
      return 'Pallet rack';
    case 'desk':
      return 'Desk';
    case 'bench':
      return 'Bench';
    case 'machine':
      return 'Machine';
    case 'square':
      return 'Square';
    case 'circle':
      return 'Circle';
    case 'table':
      return 'Table';
    case 'counter':
      return 'Counter';
    case 'plant':
      return 'Plant';
    case 'couch':
      return 'Couch';
    case 'chair':
      return 'Chair';
    case 'coffee':
      return 'Coffee';
    case 'whiteboard':
      return 'Whiteboard';
    case 'pingpong':
      return 'Ping pong';
    case 'zone':
      return 'Room';
    case 'station':
      return 'Workstation';
    case 'door':
      return 'Door';
  }
}

/** Drop a new piece centred on (cx, cy). Stations are handled by the page. */
export function addPlacedItem(
  layout: OfficeLayout,
  kind: Exclude<PlaceKind, 'station'>,
  cx: number,
  cy: number,
): { layout: OfficeLayout; selection: LayoutSelection } {
  const next = cloneLayout(layout);
  const size = PLACE_SIZE[kind];
  const x = cx - size.w / 2;
  const y = cy - size.h / 2;
  const box = clampRect({ x, y, w: size.w, h: size.h }, 4, 4);

  if (kind === 'wall') {
    const piece: LayoutWall = { id: newLayoutId('wall'), ...box };
    next.walls.push(piece);
    return { layout: next, selection: { layer: 'wall', id: piece.id } };
  }
  if (kind === 'zone') {
    const n =
      next.zones.filter((z) => z.id.startsWith('zone-') || /^Room/.test(z.label)).length + 1;
    const piece: Zone = {
      id: newLayoutId('zone'),
      label: `Room ${n}`,
      showLabel: true,
      ...box,
    };
    next.zones.unshift(piece);
    return { layout: next, selection: { layer: 'zone', id: piece.id } };
  }
  if (kind === 'door') {
    const piece: LayoutDoor = { id: newLayoutId('door'), kind: 'door', ...box };
    next.doors.push(piece);
    return { layout: next, selection: { layer: 'door', id: piece.id } };
  }
  const furnKind = FURNITURE_PLACE[kind];
  if (!furnKind) return { layout, selection: { layer: 'furniture', id: '' } };
  const piece: LayoutFurniture = {
    id: newLayoutId('furn'),
    kind: furnKind,
    solid: SOLID_DEFAULT[furnKind] === false ? false : true,
    facing: furnKind === 'desk' || furnKind === 'couch' ? 'down' : undefined,
    label:
      furnKind === 'machine' ||
      furnKind === 'square' ||
      furnKind === 'circle' ||
      furnKind === 'pingpong'
        ? placeLabel(kind)
        : undefined,
    ...box,
  };
  next.furniture.push(piece);
  return { layout: next, selection: { layer: 'furniture', id: piece.id } };
}

export function getSelectedRect(
  layout: OfficeLayout,
  sel: LayoutSelection,
  stationRect?: Rect | null,
): Rect | null {
  if (sel.layer === 'station') return stationRect ?? null;
  if (sel.layer === 'wall') {
    const w = layout.walls.find((x) => x.id === sel.id);
    return w ? { x: w.x, y: w.y, w: w.w, h: w.h } : null;
  }
  if (sel.layer === 'furniture') {
    const f = layout.furniture.find((x) => x.id === sel.id);
    return f ? { x: f.x, y: f.y, w: f.w, h: f.h } : null;
  }
  if (sel.layer === 'zone') {
    const z = layout.zones.find((x) => x.id === sel.id);
    return z ? { x: z.x, y: z.y, w: z.w, h: z.h } : null;
  }
  if (sel.layer === 'door') {
    const d = layout.doors.find((x) => x.id === sel.id);
    return d ? { x: d.x, y: d.y, w: d.w, h: d.h } : null;
  }
  const f = layout.floors.find((x) => x.id === sel.id);
  return f ? { x: f.x, y: f.y, w: f.w, h: f.h } : null;
}

export function applyRect(layout: OfficeLayout, sel: LayoutSelection, rect: Rect): OfficeLayout {
  const next = cloneLayout(layout);
  const min = minSizeFor(sel.layer);
  const box = clampRect(rect, min.w, min.h);
  if (sel.layer === 'wall') {
    const w = next.walls.find((x) => x.id === sel.id);
    if (w) Object.assign(w, box);
  } else if (sel.layer === 'furniture') {
    const f = next.furniture.find((x) => x.id === sel.id);
    if (f) Object.assign(f, box);
  } else if (sel.layer === 'zone') {
    const z = next.zones.find((x) => x.id === sel.id);
    if (z) {
      Object.assign(z, box);
      if (z.labelAt) {
        z.labelAt = { x: box.x + box.w / 2, y: box.y + box.h / 2 };
      }
    }
  } else if (sel.layer === 'door') {
    const d = next.doors.find((x) => x.id === sel.id);
    if (d) Object.assign(d, box);
  } else if (sel.layer === 'floor') {
    const f = next.floors.find((x) => x.id === sel.id);
    if (f) Object.assign(f, box);
  }
  return next;
}

export function removeSelected(layout: OfficeLayout, sel: LayoutSelection): OfficeLayout {
  if (sel.layer === 'station') return layout;
  const next = cloneLayout(layout);
  if (sel.layer === 'wall') next.walls = next.walls.filter((x) => x.id !== sel.id);
  if (sel.layer === 'furniture') next.furniture = next.furniture.filter((x) => x.id !== sel.id);
  if (sel.layer === 'zone') next.zones = next.zones.filter((x) => x.id !== sel.id);
  if (sel.layer === 'door') next.doors = next.doors.filter((x) => x.id !== sel.id);
  if (sel.layer === 'floor') next.floors = next.floors.filter((x) => x.id !== sel.id);
  return next;
}

export function duplicateSelected(
  layout: OfficeLayout,
  sel: LayoutSelection,
): {
  layout: OfficeLayout;
  selection: LayoutSelection;
} | null {
  if (sel.layer === 'station') return null;
  const next = cloneLayout(layout);
  const offset = 14;
  if (sel.layer === 'wall') {
    const src = next.walls.find((x) => x.id === sel.id);
    if (!src) return null;
    const copy = { ...src, id: newLayoutId('wall'), x: src.x + offset, y: src.y + offset };
    next.walls.push(copy);
    return { layout: next, selection: { layer: 'wall', id: copy.id } };
  }
  if (sel.layer === 'furniture') {
    const src = next.furniture.find((x) => x.id === sel.id);
    if (!src) return null;
    const copy = { ...src, id: newLayoutId('furn'), x: src.x + offset, y: src.y + offset };
    next.furniture.push(copy);
    return { layout: next, selection: { layer: 'furniture', id: copy.id } };
  }
  if (sel.layer === 'zone') {
    const src = next.zones.find((x) => x.id === sel.id);
    if (!src) return null;
    const copy: Zone = {
      ...src,
      id: newLayoutId('zone'),
      label: `${src.label} copy`,
      x: src.x + offset,
      y: src.y + offset,
      labelAt: src.labelAt ? { x: src.labelAt.x + offset, y: src.labelAt.y + offset } : undefined,
    };
    next.zones.unshift(copy);
    return { layout: next, selection: { layer: 'zone', id: copy.id } };
  }
  if (sel.layer === 'door') {
    const src = next.doors.find((x) => x.id === sel.id);
    if (!src) return null;
    const copy = { ...src, id: newLayoutId('door'), x: src.x + offset, y: src.y + offset };
    next.doors.push(copy);
    return { layout: next, selection: { layer: 'door', id: copy.id } };
  }
  return null;
}

export function applyResize(
  start: Rect,
  handle: ResizeHandle,
  dx: number,
  dy: number,
  minW: number,
  minH: number,
): Rect {
  let { x, y, w, h } = start;
  if (handle.includes('e')) w = start.w + dx;
  if (handle.includes('s')) h = start.h + dy;
  if (handle.includes('w')) {
    w = start.w - dx;
    x = start.x + dx;
  }
  if (handle.includes('n')) {
    h = start.h - dy;
    y = start.y + dy;
  }
  if (w < minW) {
    if (handle.includes('w')) x = start.x + start.w - minW;
    w = minW;
  }
  if (h < minH) {
    if (handle.includes('n')) y = start.y + start.h - minH;
    h = minH;
  }
  return { x, y, w, h };
}

export function handleAt(
  rect: Rect,
  wx: number,
  wy: number,
  handleSize: number,
): ResizeHandle | null {
  const hs = handleSize;
  const cx = rect.x + rect.w / 2;
  const cy = rect.y + rect.h / 2;
  const points: Array<[ResizeHandle, number, number]> = [
    ['nw', rect.x, rect.y],
    ['n', cx, rect.y],
    ['ne', rect.x + rect.w, rect.y],
    ['e', rect.x + rect.w, cy],
    ['se', rect.x + rect.w, rect.y + rect.h],
    ['s', cx, rect.y + rect.h],
    ['sw', rect.x, rect.y + rect.h],
    ['w', rect.x, cy],
  ];
  for (const [h, px, py] of points) {
    if (Math.abs(wx - px) <= hs && Math.abs(wy - py) <= hs) return h;
  }
  return null;
}

export function cursorForHandle(handle: ResizeHandle | null, canMove: boolean): string {
  if (!handle) return canMove ? 'move' : '';
  switch (handle) {
    case 'n':
    case 's':
      return 'ns-resize';
    case 'e':
    case 'w':
      return 'ew-resize';
    case 'ne':
    case 'sw':
      return 'nesw-resize';
    case 'nw':
    case 'se':
      return 'nwse-resize';
  }
}

const HANDLE_CURSOR_CYCLE: readonly ResizeHandle[] = ['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw'];

export function canRotate(layer: LayoutLayer): boolean {
  return layer === 'furniture' || layer === 'wall' || layer === 'door' || layer === 'station';
}

export function rotateHandleGap(zoom: number): number {
  return Math.max(10, 14 / zoom);
}

export function rotateHandlePos(rect: Rect, deg: number, gap: number): { x: number; y: number } {
  const c = rectCenter(rect);
  return rotatePoint(c.x, rect.y - gap, c.x, c.y, deg);
}

export function handleAtRotated(
  rect: Rect,
  deg: number,
  wx: number,
  wy: number,
  handleSize: number,
  gap: number,
  includeRotate: boolean,
): ResizeHandle | 'rotate' | null {
  const hs = handleSize;
  if (includeRotate) {
    const rh = rotateHandlePos(rect, deg, gap);
    if (Math.hypot(wx - rh.x, wy - rh.y) <= hs * 1.15) return 'rotate';
  }
  const c = rectCenter(rect);
  const points: Array<[ResizeHandle, number, number]> = [
    ['nw', rect.x, rect.y],
    ['n', c.x, rect.y],
    ['ne', rect.x + rect.w, rect.y],
    ['e', rect.x + rect.w, c.y],
    ['se', rect.x + rect.w, rect.y + rect.h],
    ['s', c.x, rect.y + rect.h],
    ['sw', rect.x, rect.y + rect.h],
    ['w', rect.x, c.y],
  ];
  for (const [h, px, py] of points) {
    const p = rotatePoint(px, py, c.x, c.y, deg);
    if (Math.abs(wx - p.x) <= hs && Math.abs(wy - p.y) <= hs) return h;
  }
  return null;
}

export function cursorForHandleRotated(
  handle: ResizeHandle | 'rotate' | null,
  deg: number,
  canMove: boolean,
): string {
  if (handle === 'rotate') return 'grab';
  if (!handle) return canMove ? 'move' : '';
  const idx = HANDLE_CURSOR_CYCLE.indexOf(handle);
  const shift = Math.round(normalizeDeg(deg) / 45);
  const rotated =
    HANDLE_CURSOR_CYCLE[
      (idx + shift + HANDLE_CURSOR_CYCLE.length * 4) % HANDLE_CURSOR_CYCLE.length
    ];
  return cursorForHandle(rotated, canMove);
}

function oppositePoint(r: Rect, handle: ResizeHandle): { x: number; y: number } {
  switch (handle) {
    case 'e':
      return { x: r.x, y: r.y + r.h / 2 };
    case 'w':
      return { x: r.x + r.w, y: r.y + r.h / 2 };
    case 'n':
      return { x: r.x + r.w / 2, y: r.y + r.h };
    case 's':
      return { x: r.x + r.w / 2, y: r.y };
    case 'ne':
      return { x: r.x, y: r.y + r.h };
    case 'nw':
      return { x: r.x + r.w, y: r.y + r.h };
    case 'se':
      return { x: r.x, y: r.y };
    case 'sw':
      return { x: r.x + r.w, y: r.y };
  }
}

/** Resize a rotated piece in local axes, keeping the opposite handle fixed in world space. */
export function applyResizeRotated(
  start: Rect,
  handle: ResizeHandle,
  worldDx: number,
  worldDy: number,
  deg: number,
  minW: number,
  minH: number,
): Rect {
  if (!deg) return applyResize(start, handle, worldDx, worldDy, minW, minH);
  const local = worldDeltaToLocal(worldDx, worldDy, deg);
  const resized = applyResize(start, handle, local.dx, local.dy, minW, minH);
  const c0 = rectCenter(start);
  const c1 = rectCenter(resized);
  const opp0 = oppositePoint(start, handle);
  const opp1 = oppositePoint(resized, handle);
  const world0 = rotatePoint(opp0.x, opp0.y, c0.x, c0.y, deg);
  const world1 = rotatePoint(opp1.x, opp1.y, c1.x, c1.y, deg);
  return { ...resized, x: resized.x + world0.x - world1.x, y: resized.y + world0.y - world1.y };
}

export function dragRotation(
  start: Rect,
  startRotation: number,
  origin: { x: number; y: number },
  pointer: { x: number; y: number },
  fine: boolean,
): number {
  const c = rectCenter(start);
  const delta = angleDeg(c.x, c.y, pointer.x, pointer.y) - angleDeg(c.x, c.y, origin.x, origin.y);
  return snapRotation(startRotation + delta, fine);
}

export function getSelectedRotation(
  layout: OfficeLayout,
  sel: LayoutSelection,
  stationRotation?: number,
): number {
  if (sel.layer === 'station') return normalizeDeg(stationRotation ?? 0);
  if (sel.layer === 'furniture') {
    const f = layout.furniture.find((x) => x.id === sel.id);
    return f ? pieceRotation(f) : 0;
  }
  if (sel.layer === 'wall') {
    const w = layout.walls.find((x) => x.id === sel.id);
    return w ? pieceRotation(w) : 0;
  }
  if (sel.layer === 'door') {
    const d = layout.doors.find((x) => x.id === sel.id);
    return d ? pieceRotation(d) : 0;
  }
  return 0;
}

export function applyRotation(
  layout: OfficeLayout,
  sel: LayoutSelection,
  rotation: number,
): OfficeLayout {
  if (!canRotate(sel.layer) || sel.layer === 'station') return layout;
  const next = cloneLayout(layout);
  const deg = normalizeDeg(rotation);
  const stored = deg === 0 ? undefined : deg;
  if (sel.layer === 'wall') {
    const w = next.walls.find((x) => x.id === sel.id);
    if (w) w.rotation = stored;
  } else if (sel.layer === 'furniture') {
    const f = next.furniture.find((x) => x.id === sel.id);
    if (f) {
      f.rotation = stored;
      if (f.kind === 'desk' || f.kind === 'couch') f.facing = rotationToFacing(deg);
    }
  } else if (sel.layer === 'door') {
    const d = next.doors.find((x) => x.id === sel.id);
    if (d) d.rotation = stored;
  }
  return next;
}

export function applyTransform(
  layout: OfficeLayout,
  sel: LayoutSelection,
  rect: Rect,
  rotation: number,
): OfficeLayout {
  if (sel.layer === 'station') return layout;
  return applyRotation(applyRect(layout, sel, rect), sel, rotation);
}

function pointIn(r: Rect, x: number, y: number, pad = 0): boolean {
  return x >= r.x - pad && x <= r.x + r.w + pad && y >= r.y - pad && y <= r.y + r.h + pad;
}

/**
 * Top-most editable piece under a world point. Stations are tested by the
 * canvas because their rects aren't in the layout document.
 */
export function hitTestLayout(layout: OfficeLayout, x: number, y: number): LayoutSelection | null {
  for (let i = layout.furniture.length - 1; i >= 0; i--) {
    const f = layout.furniture[i];
    if (pointInRotatedRect(f, pieceRotation(f), x, y, 1)) return { layer: 'furniture', id: f.id };
  }
  for (let i = layout.doors.length - 1; i >= 0; i--) {
    const d = layout.doors[i];
    if (pointInRotatedRect(d, pieceRotation(d), x, y, 2)) return { layer: 'door', id: d.id };
  }
  for (let i = layout.walls.length - 1; i >= 0; i--) {
    const w = layout.walls[i];
    // Thin walls are hard to click — inflate the hit box a little.
    const pad = Math.max(0, 3 - Math.min(w.w, w.h) / 2);
    if (pointInRotatedRect(w, pieceRotation(w), x, y, pad)) return { layer: 'wall', id: w.id };
  }
  for (const z of layout.zones) {
    if (z.showLabel === false) continue;
    if (pointIn(z, x, y)) return { layer: 'zone', id: z.id };
  }
  return null;
}

export function furnitureById(layout: OfficeLayout, id: string): LayoutFurniture | undefined {
  return layout.furniture.find((f) => f.id === id);
}

export function zoneById(layout: OfficeLayout, id: string): Zone | undefined {
  return layout.zones.find((z) => z.id === id);
}

export function patchFurniture(
  layout: OfficeLayout,
  id: string,
  patch: Partial<Pick<LayoutFurniture, 'label' | 'kind' | 'solid' | 'facing' | 'rotation'>>,
): OfficeLayout {
  const next = cloneLayout(layout);
  const f = next.furniture.find((x) => x.id === id);
  if (!f) return layout;
  if (patch.label !== undefined) f.label = patch.label || undefined;
  if (patch.kind !== undefined) f.kind = patch.kind;
  if (patch.solid !== undefined) f.solid = patch.solid;
  if (patch.rotation !== undefined) {
    const deg = normalizeDeg(patch.rotation);
    f.rotation = deg === 0 ? undefined : deg;
    if (f.kind === 'desk' || f.kind === 'couch') f.facing = rotationToFacing(deg);
  } else if (patch.facing !== undefined) {
    f.facing = patch.facing;
    const deg = facingToRotation(patch.facing);
    f.rotation = deg === 0 ? undefined : deg;
  }
  return next;
}

export function patchZone(
  layout: OfficeLayout,
  id: string,
  patch: Partial<Pick<Zone, 'label' | 'showLabel'>>,
): OfficeLayout {
  const next = cloneLayout(layout);
  const z = next.zones.find((x) => x.id === id);
  if (!z) return layout;
  if (patch.label !== undefined) z.label = patch.label;
  if (patch.showLabel !== undefined) z.showLabel = patch.showLabel;
  return next;
}

export function setStationSize(
  layout: OfficeLayout,
  stationId: string,
  size: { w: number; h: number; rotation?: number } | null,
): OfficeLayout {
  const next = cloneLayout(layout);
  if (!next.stationSizes) next.stationSizes = {};
  if (!size) {
    delete next.stationSizes[stationId];
    return next;
  }
  const prev = next.stationSizes[stationId];
  const rotation = size.rotation !== undefined ? normalizeDeg(size.rotation) : prev?.rotation;
  next.stationSizes[stationId] = {
    w: Math.max(16, size.w),
    h: Math.max(8, size.h),
    ...(rotation ? { rotation } : {}),
  };
  return next;
}
